import { Add, FolderCopyOutlined } from '@mui/icons-material';
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { RefObject, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { url } from '../../../appRouter/url';
import { type ThemaFromThemaRouter } from '../../../dtos';
import { themaDetailsPageContent } from '../../../pages/ThemaDetailsPage/ThemaDetailsPage.content.ts';
import { trpc } from '../../../trpc.ts';
import { isAblaufTemplateMissing, isBlendedLearning, printCservFormat } from '../../../utils';
import { isInViewport } from '../../../utils/isInViewport.ts';
import { getPendantLabel } from '../../../utils/isOnline.ts';
import { LabelValue } from '../LabelValue.tsx';
import { StickyWrapperWithBlur } from '../StickyWrapper.tsx';
import { LeistungTag } from './LeistungTag.tsx';
import { NavigationTab } from './NavigationTab.tsx';
import { content } from './ThemaHeader.content';
import { getOnlinePraesenzTag } from './ThemaHeader.utils.tsx';

const isAnlegenAllowed = (
  thema: ThemaFromThemaRouter,
): { enabled: true; disabledReason: null } | { enabled: false; disabledReason: string } => {
  const hasNoLeistungstemplates = thema.leistungstemplates.length === 0;

  if (hasNoLeistungstemplates && !thema.manuellerAusschreibungsprozess) {
    return {
      enabled: false,
      disabledReason: themaDetailsPageContent.tooltips.missingLeistungstemplates,
    };
  }

  if (!isBlendedLearning(thema.strukturellesFormat) && isAblaufTemplateMissing(thema)) {
    return {
      enabled: false,
      disabledReason: themaDetailsPageContent.tooltips.missingAblaufTemplate,
    };
  }

  return { enabled: true, disabledReason: null };
};

type ThemaHeaderProps = {
  readonly thema: ThemaFromThemaRouter;
  readonly navigation: { label: string; ref: RefObject<HTMLElement | null> }[];
};

export const ThemaHeader: React.FC<ThemaHeaderProps> = ({ thema, navigation }: ThemaHeaderProps) => {
  const navigate = useNavigate();

  const { enabled: anlegenAllowed, disabledReason: anlegenDisabledReason } = isAnlegenAllowed(thema);

  const handleClick = (): void => {
    if (isBlendedLearning(thema.strukturellesFormat)) {
      void navigate(url.thema.durchgangPlanung(thema.akaProduktId));
    } else {
      void navigate(url.thema.veranstaltungPlanung(thema.akaProduktId));
    }
  };

  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  const calculateCurrentTabIndex = () => {
    setCurrentTabIndex(navigation.findIndex((nav) => isInViewport(nav.ref)));
  };

  const entityLabel = isBlendedLearning(thema.strukturellesFormat) ? 'Durchgang' : 'Veranstaltung';

  const linkedThema = trpc.thema.getThemaByAkaProduktId.useQuery(thema.verknuepfungBlopUndPraesenzseminar ?? '', {
    enabled: Boolean(thema.verknuepfungBlopUndPraesenzseminar),
  });

  // useEffect to add scroll listening
  useEffect(() => {
    document.addEventListener('scroll', calculateCurrentTabIndex, true);
    return () => document.removeEventListener('scroll', calculateCurrentTabIndex, true);
  });

  return (
    <StickyWrapperWithBlur sx={{ width: '100%' }}>
      <Stack
        sx={{
          position: 'relative',
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Paper
          sx={{
            p: 2,
            backgroundColor: (theme) => theme.palette.text.brand,
          }}
        >
          <Stack gap={2}>
            <Stack gap={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box display="flex" gap={2} alignItems="center">
                  <FolderCopyOutlined fontSize="large" sx={{ color: 'text.secondaryOnBrand' }} />
                  <Typography variant="headingXs" color="textSecondaryOnBrand">
                    {thema.titel}
                  </Typography>
                  <div className="flex gap-4">
                    {getOnlinePraesenzTag(thema)}
                    <LeistungTag leistungtemplates={thema.leistungstemplates} />
                  </div>
                </Box>

                <Tooltip title={anlegenDisabledReason} placement="left" arrow disableInteractive>
                  <Box>
                    <Button
                      disabled={!anlegenAllowed}
                      onClick={handleClick}
                      variant="contained"
                      size="small"
                      sx={(theme) => ({
                        background: theme.palette.surface.background,
                        '&:hover': { background: theme.palette.surface.brandLightHover },
                        '&:disabled': {
                          background: theme.palette.surface.disabledSolid,
                          color: theme.palette.text.disabledSolid,
                        },
                        color: 'text.primary',
                        py: 0.75,
                        fontSize: '12px',
                      })}
                      startIcon={<Add />}
                    >
                      {entityLabel} anlegen
                    </Button>
                  </Box>
                </Tooltip>
              </Stack>
              {(thema.themenblock ?? thema.themenbereich) && (
                <Typography variant="labelLight" color="textSecondaryOnBrand">
                  {thema.themenblock}&nbsp; • &nbsp;{thema.themenbereich}
                </Typography>
              )}
            </Stack>

            <Box display="grid" alignItems="center" gap={3} gridAutoFlow="column">
              <LabelValue color="brand" label={content.format} value={printCservFormat(thema)} />
              <LabelValue color="brand" label={content.buchungsnummer} value={thema.buchungsnummer} />
              {thema.verknuepfungBlopUndPraesenzseminar && linkedThema.data?.thema && (
                <LabelValue
                  color="brand"
                  label={getPendantLabel(linkedThema.data.thema)}
                  value={
                    <Link
                      to={url.thema.details(thema.verknuepfungBlopUndPraesenzseminar)}
                      target="_blank"
                      style={{
                        textDecorationColor: 'white',
                      }}
                    >
                      <Typography color="textPrimaryOnBrand" variant="labelLight" component="p">
                        {thema.verknuepfungBlopUndPraesenzseminar}
                      </Typography>
                    </Link>
                  }
                />
              )}

              {thema.planungsinformation?.musskopplungReihenfolgeAkaProduktIds &&
                thema.planungsinformation.musskopplungReihenfolgeAkaProduktIds.length > 0 && (
                  <LabelValue
                    label="Muss-Kopplung"
                    value={
                      <Box display="flex" gap={1}>
                        {thema.planungsinformation.musskopplungReihenfolgeAkaProduktIds.map((akaProduktId) => (
                          <Link
                            to={url.thema.details(akaProduktId)}
                            key={akaProduktId}
                            style={{ textDecorationColor: 'white' }}
                          >
                            <Typography variant="labelLight" color="textPrimaryOnBrand">
                              {akaProduktId}
                            </Typography>
                          </Link>
                        ))}
                      </Box>
                    }
                    color="brand"
                  />
                )}
            </Box>
          </Stack>
        </Paper>

        <Paper
          sx={{
            mt: 1,
            width: '100%',
            borderColor: 'transparent',
            boxShadow:
              '0 0 1px 1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Stack direction="row" justifyContent="space-between" sx={{ px: 2, py: 1 }}>
            {navigation.map((nav, index) => (
              <NavigationTab key={nav.label} ref={nav.ref} isSelected={currentTabIndex === index} label={nav.label} />
            ))}
          </Stack>
        </Paper>
      </Stack>
    </StickyWrapperWithBlur>
  );
};
